// ============================================================================
// FSA-2.2 multi-column class table — formula evaluator (mirror of
// FsaStatementEvaluator.EvaluateFormula and the disclosure-grid evaluator)
// ============================================================================
// Linear `+` / `-` formula evaluation over named column codes. Tokens are
// either operators ('+' or '-') or column codes resolved from the same
// row's column_values dict. Unknown tokens silently contribute zero —
// keeps the evaluator resilient to typos / missing inputs while the
// auditor types. Matches the backend's tokenizer one-to-one so client
// and server agree.
//
// Future-proofing note: only +/- and column-code references are
// supported today. If we need parentheses, multiplication, literals,
// or aggregate functions later, extend BOTH this file AND the C# side
// in lockstep — the contract is that whatever the UI computes matches
// what the server validates on save.

/**
 * Evaluate a formula like "gross_opening + gross_additions - gross_deletions"
 * against the supplied column-code map. Returns 0 when:
 *   • formula is empty / whitespace
 *   • no operands resolve to a value (all tokens unknown)
 *   • the formula contains only operators
 *
 * Sign convention: leading '+' or implicit positive. '-' negates the
 * NEXT operand. Successive operators ('+ -' / '+ +') collapse to the
 * last one (matching the backend's "reset on operand" semantics).
 *
 * FSA-2.2 `is_deduction` flag (optional 3rd argument): when a column's
 * spec carries `is_deduction: true`, its value is **negated** at
 * operand-resolve time. Lets formulas declare deduction columns
 * declaratively rather than relying on the author having written `-`
 * in the formula string. Stacks with the operator sign — `+ x` on an
 * is_deduction column subtracts; `- x` on the same column adds (rare
 * but consistent).
 */
export function evaluateFormula(
  formula: string | null | undefined,
  values: Record<string, number> | null | undefined,
  columnSpecByCode?: Record<string, { is_deduction?: boolean }> | null,
): number {
  if (!formula) return 0;
  const tokens = formula.trim().split(/\s+/).filter(t => t.length > 0);
  if (tokens.length === 0) return 0;
  let total = 0;
  let sign = 1;
  for (const t of tokens) {
    if (t === '+') { sign = 1; continue; }
    if (t === '-') { sign = -1; continue; }
    // Operand. Resolve from the row's column_values map; unknown codes
    // are silently zero (intentional — see file header).
    const v = values?.[t];
    if (typeof v === 'number' && Number.isFinite(v)) {
      const deductionFlip = columnSpecByCode?.[t]?.is_deduction ? -1 : 1;
      total += sign * deductionFlip * v;
    }
    sign = 1; // implicit "+" after each operand, matching backend
  }
  return total;
}
